import { useState, useEffect, useCallback } from "react";
import api from "../../api/client";
import useAuth from "../../hooks/useAuth";
import useCompositionGuard from "../../hooks/useCompositionGuard";
import { extractErrorMessage, getLocalToday, getLocalTomorrow } from "../../utils/helpers";
import TeamPersonnelGroup from "../common/TeamPersonnelGroup";
import NapFieldsGroup, { napFieldsComplete, vacantPortsPayload } from "../common/NapFieldsGroup";
import NapCompletionCard from "../common/NapCompletionCard";
import ButtonSpinner from "../common/ButtonSpinner";
import FetchingOverlay from "../common/FetchingOverlay";

/* ───────────── Constants ───────────── */

const DECOMM_OPTIONS = ["Y", "N"];
const CALL_STATUS_OPTIONS = [
  "Confirmed", "KOR", "CBR", "Cancelled", "Unattended", "Busy tone", "Others"
];
const DISPATCH_REQUIRED_STATUSES = new Set(["Confirmed"]);

const CALL_CANCEL_REASON_OPTIONS = [
  "Cancelled by Subs",
  "Full NAP",
  "For New Installation",
  "Already Migrated",
  "Other reason",
];

const CANCELLATION_REASON_OPTIONS = [
  "Cancelled by Subs",
  "Needs RTA",
  "Full NAP",
  "No Converge Facility",
  "For NI",
  "Other reason",
];

const RESCHEDULE_REASON_OPTIONS = [
  "Needs permit",
  "Requested by customer",
  "Subs not around",
  "Other reason",
];

const STAGE_LABELS = {
  uploaded_for_migration: "Uploaded for Migration",
  for_dispatch: "For Dispatch",
  visited_called: "Completed/Visited/Called",
  for_calling_scheduling: "For Calling/Scheduling",
};

const TERMINAL_ACTIONS = new Set(["Completed", "Cancelled"]);

/* ───────────── Helpers ───────────── */

function fmtCallStatus(record) {
  if (!record?.call_status) return null;
  if (record.call_status === "Others" && record.call_status_other_text?.trim()) {
    return `Other - ${record.call_status_other_text.trim()}`;
  }
  return record.call_status;
}

function toInputDate(val) {
  if (!val) return "";
  const d = new Date(val);
  if (isNaN(d)) return val;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function localToday() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/* ───────────── Shared small components ───────────── */

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-0.5">{label}</label>
      {children}
    </div>
  );
}

function MetaField({ label, value }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-0.5">{label}</label>
      <p className="text-sm text-gray-800 bg-white rounded px-3 py-1.5 border">{value || "—"}</p>
    </div>
  );
}

function Input({ value, onChange, disabled, type = "text", placeholder }) {
  return (
    <input
      type={type}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      placeholder={placeholder}
      className="w-full border rounded px-3 py-2 text-sm disabled:bg-gray-50 disabled:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
    />
  );
}

function ReadValue({ value }) {
  return (
    <p className="text-sm text-gray-900 bg-gray-50 rounded px-3 py-2 border min-h-[36px]">
      {value || <span className="text-gray-400">—</span>}
    </p>
  );
}

function Select({ value, onChange, options, disabled, placeholder = "— Select —" }) {
  return (
    <select
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className="w-full border rounded px-3 py-2 text-sm disabled:bg-gray-50 disabled:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
    >
      <option value="">{placeholder}</option>
      {options.map((o) => (
        <option key={o} value={o}>{o}</option>
      ))}
    </select>
  );
}

function CollapsibleSection({ title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border rounded-lg overflow-hidden mt-4 col-span-full">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-2 bg-gray-50 hover:bg-gray-100 text-left text-sm font-medium text-gray-700"
      >
        {title}
        <span className="text-gray-400 text-xs">{open ? "▲" : "▼"}</span>
      </button>
      {open && <div className="px-4 py-4 grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>}
    </div>
  );
}

/* ───────────── Popup Dialogs ───────────── */

function CompleteDialog({ record, companyId, onConfirm, onCancel, submitting = false }) {
  const [joNumber, setJoNumber] = useState(record?.jo_number || "");
  const [newAccountNumber, setNewAccountNumber] = useState(record?.new_account_number || "");
  const [remark, setRemark] = useState(record?.remarks || "");
  const [serialNumber, setSerialNumber] = useState(record?.modem_serial_number || "");
  const [recordIdField, setRecordIdField] = useState(record?.iptv_serial_number || "");
  const [napCode, setNapCode] = useState(record?.nap_code || "");
  const [napId, setNapId] = useState(record?.nap_id || "");
  const [napPort, setNapPort] = useState(record?.nap_port || "");
  const [vacantPorts, setVacantPorts] = useState(record?.vacant_ports ?? "");
  const [napPhoto, setNapPhoto] = useState(null);
  const { check: checkComp, ConflictDialog } = useCompositionGuard();

  const canConfirm =
    joNumber.trim() && newAccountNumber.trim() && remark.trim() && serialNumber.trim() &&
    napFieldsComplete({ napCode, napId, napPort, vacantPorts }) && !!napPhoto;

  const handleConfirmComplete = async () => {
    if (!canConfirm) return;
    const ok = await checkComp({ teamNumber: record?.team_number, assignmentDate: record?.dispatch_date, personnel1Id: record?.personnel_1_id, personnel2Id: record?.personnel_2_id });
    if (!ok) return;
    onConfirm({ team_number: record?.team_number, personnel_1_id: record?.personnel_1_id, personnel_2_id: record?.personnel_2_id, jo_number: joNumber, new_account_number: newAccountNumber, remarks: remark, modem_serial_number: serialNumber, iptv_serial_number: recordIdField || null, nap_code: napCode, nap_id: napId, nap_port: napPort, vacant_ports: vacantPortsPayload(vacantPorts) }, napPhoto);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40">
      <FetchingOverlay show={submitting} />
      <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 flex flex-col max-h-[90vh]">
        <div className="px-6 py-4 border-b shrink-0">
          <h3 className="text-lg font-semibold mb-2">Complete Migration Record</h3>
          <p className="text-sm text-gray-600">Are you sure you want to mark this record as Completed?</p>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="space-y-3">
            <MetaField label="Team Number" value={record?.team_number} />
            <MetaField label="Personnel 1" value={record?.personnel || ""} />
            {record?.personnel_2_id && <MetaField label="Personnel 2" value={record?.personnel_2 || ""} />}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-0.5">JO Number <span className="text-red-500">*</span></label>
              <input type="text" value={joNumber} onChange={(e) => setJoNumber(e.target.value)} placeholder="Enter JO Number..." className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-0.5">New Account Number <span className="text-red-500">*</span></label>
              <input type="text" value={newAccountNumber} onChange={(e) => setNewAccountNumber(e.target.value)} placeholder="Enter New Account Number..." className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-0.5">JO Status Remarks <span className="text-red-500">*</span></label>
              <textarea value={remark} onChange={(e) => setRemark(e.target.value)} rows={3} placeholder="Enter JO Status Remarks..." className="w-full border rounded px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <NapFieldsGroup
                napCode={napCode} onNapCodeChange={setNapCode}
                napId={napId} onNapIdChange={setNapId}
                napPort={napPort} onNapPortChange={setNapPort}
                vacantPorts={vacantPorts} onVacantPortsChange={setVacantPorts}
                showPhoto napPhoto={napPhoto} onNapPhotoChange={setNapPhoto}
                labelClassName="block text-xs font-medium text-gray-500 mb-0.5"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-0.5">Modem Serial Number <span className="text-red-500">*</span></label>
              <input type="text" value={serialNumber} onChange={(e) => setSerialNumber(e.target.value)} placeholder="Enter Modem Serial Number..." className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-0.5">IPTV Serial Number</label>
              <input type="text" value={recordIdField} onChange={(e) => setRecordIdField(e.target.value)} placeholder="Enter IPTV Serial Number..." className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>
        </div>
        <div className="sticky bottom-0 flex justify-end gap-2 px-6 py-4 border-t bg-white shrink-0">
          <button type="button" onClick={onCancel} disabled={submitting} className="px-4 py-2 text-sm border rounded hover:bg-gray-100 disabled:opacity-50">Cancel</button>
          <button type="button" disabled={submitting || !canConfirm} onClick={handleConfirmComplete} className="px-4 py-2 text-sm text-white rounded disabled:opacity-50 bg-green-600 hover:bg-green-700 inline-flex items-center gap-2">
            {submitting && <ButtonSpinner />}
            {submitting ? "Completing…" : "Confirm"}
          </button>
        </div>
      </div>
      <ConflictDialog />
    </div>
  );
}

function RescheduleDialog({ record, companyId, onConfirm, onCancel }) {
  const [teamNumber, setTeamNumber] = useState(record?.team_number || "");
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [personnel1Id, setPersonnel1Id] = useState(record?.personnel_1_id || null);
  const [personnel2Id, setPersonnel2Id] = useState(record?.personnel_2_id || null);
  const [remark, setRemark] = useState("");
  const [reasons, setReasons] = useState([]);
  const { check: checkComp, ConflictDialog } = useCompositionGuard();
  const [newDate, setNewDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  });

  // Match team on mount
  useState(() => {
    if (record?.team_number) {
      api.get("/team-masters/active", { params: { company_id: companyId } })
        .then(({ data }) => {
          const match = data.find((t) => t.team_number === record.team_number);
          if (match) setSelectedTeamId(match.id);
        }).catch(() => {});
    }
  });

  const toggleReason = (r) => setReasons((prev) => prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]);

  const buildFinalRemark = () => {
    const prefix = reasons.length > 0 ? reasons.join("; ") : "";
    const text = remark.trim();
    if (prefix && text) return `${prefix} - ${text}`;
    return text || "";
  };

  const minDate = (() => {
    const t = localToday();
    const [y, m, d] = t.split("-").map(Number);
    const nd = new Date(y, m - 1, d + 1);
    return `${nd.getFullYear()}-${String(nd.getMonth() + 1).padStart(2, "0")}-${String(nd.getDate()).padStart(2, "0")}`;
  })();

  const canConfirm = remark.trim() && newDate && reasons.length > 0;

  const handleConfirmReschedule = async () => {
    if (!canConfirm) return;
    const ok = await checkComp({ teamNumber, assignmentDate: newDate, personnel1Id, personnel2Id });
    if (!ok) return;
    onConfirm({ team_number: teamNumber || null, personnel_1_id: personnel1Id, personnel_2_id: personnel2Id, remarks: buildFinalRemark(), new_dispatch_date: newDate, reschedule_reasons: reasons });
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 p-6 max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold mb-2">Reschedule Migration Record</h3>
        <p className="text-sm text-gray-600 mb-4">Are you sure you want to reschedule this record?</p>
        <TeamPersonnelGroup
          teamMasterId={selectedTeamId}
          personnel1Id={personnel1Id}
          personnel2Id={personnel2Id}
          onTeamChange={(teamId, teamData) => {
            setSelectedTeamId(teamId);
            if (teamData) setTeamNumber(teamData.team_number);
            else setTeamNumber("");
          }}
          onPersonnel1Change={(id) => setPersonnel1Id(id)}
          onPersonnel2Change={(id) => setPersonnel2Id(id)}
          companyId={companyId}
          assignmentDate={record?.dispatch_date}
        />
        <label className="block text-sm font-medium mb-1">JO Status Remarks <span className="text-red-500">*</span></label>
        <textarea value={remark} onChange={(e) => setRemark(e.target.value)} rows={3} placeholder="Enter JO Status Remarks..." className="w-full border rounded px-3 py-2 text-sm mb-3 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500" />
        <label className="block text-sm font-medium mb-1">New Dispatch Date <span className="text-red-500">*</span></label>
        <input type="date" min={minDate} value={newDate} onChange={(e) => setNewDate(e.target.value)} className="w-full border rounded px-3 py-2 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-blue-500" />
        <label className="block text-sm font-medium mb-1">Reschedule Reasons <span className="text-red-500">*</span></label>
        <div className="space-y-1.5 mb-1">
          {RESCHEDULE_REASON_OPTIONS.map((r) => (
            <label key={r} className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
              <input type="checkbox" checked={reasons.includes(r)} onChange={() => toggleReason(r)} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
              {r}
            </label>
          ))}
        </div>
        {reasons.length > 0 && remark.trim() && (
          <p className="text-xs text-gray-500 mt-2">Will save as: <span className="font-medium">{buildFinalRemark()}</span></p>
        )}
        {!canConfirm && (
          <p className="text-xs text-red-500 mt-2">
            {!remark.trim() && reasons.length === 0 ? "Please fill in all required fields." : !remark.trim() ? "Please enter JO Status Remarks." : reasons.length === 0 ? "Please select at least one reschedule reason." : "Please select a new dispatch date."}
          </p>
        )}
        <div className="flex justify-end gap-2 mt-4">
          <button type="button" onClick={onCancel} className="px-4 py-2 text-sm border rounded hover:bg-gray-100">Cancel</button>
          <button type="button" disabled={!canConfirm} onClick={handleConfirmReschedule} className="px-4 py-2 text-sm text-white rounded disabled:opacity-50 bg-blue-600 hover:bg-blue-700">Reschedule</button>
        </div>
      </div>
      <ConflictDialog />
    </div>
  );
}

function CancelDialog({ record, companyId, onConfirm, onCancel }) {
  const [teamNumber, setTeamNumber] = useState(record?.team_number || "");
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [personnel1Id, setPersonnel1Id] = useState(record?.personnel_1_id || null);
  const [personnel2Id, setPersonnel2Id] = useState(record?.personnel_2_id || null);
  const [remark, setRemark] = useState("");
  const [reasons, setReasons] = useState([]);
  const { check: checkComp, ConflictDialog } = useCompositionGuard();

  // Match team on mount
  useState(() => {
    if (record?.team_number) {
      api.get("/team-masters/active", { params: { company_id: companyId } })
        .then(({ data }) => {
          const match = data.find((t) => t.team_number === record.team_number);
          if (match) setSelectedTeamId(match.id);
        }).catch(() => {});
    }
  });

  const toggleReason = (r) => setReasons((prev) => prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]);

  const buildFinalRemark = () => {
    const prefix = reasons.length > 0 ? reasons.join("; ") : "";
    const text = remark.trim();
    if (prefix && text) return `${prefix} - ${text}`;
    return text || "";
  };

  const canConfirm = remark.trim() && reasons.length > 0;

  const handleConfirmCancel = async () => {
    if (!canConfirm) return;
    const ok = await checkComp({ teamNumber, assignmentDate: record?.dispatch_date, personnel1Id, personnel2Id });
    if (!ok) return;
    onConfirm({ team_number: teamNumber || null, personnel_1_id: personnel1Id, personnel_2_id: personnel2Id, remarks: buildFinalRemark(), cancellation_reasons: reasons });
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 p-6 max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold mb-2">Cancel Migration Record</h3>
        <p className="text-sm text-gray-600 mb-4">Are you sure you want to cancel this record?</p>
        <TeamPersonnelGroup
          teamMasterId={selectedTeamId}
          personnel1Id={personnel1Id}
          personnel2Id={personnel2Id}
          onTeamChange={(teamId, teamData) => {
            setSelectedTeamId(teamId);
            if (teamData) setTeamNumber(teamData.team_number);
            else setTeamNumber("");
          }}
          onPersonnel1Change={(id) => setPersonnel1Id(id)}
          onPersonnel2Change={(id) => setPersonnel2Id(id)}
          companyId={companyId}
          assignmentDate={record?.dispatch_date}
        />
        <label className="block text-sm font-medium mb-1">JO Status Remarks <span className="text-red-500">*</span></label>
        <textarea value={remark} onChange={(e) => setRemark(e.target.value)} rows={3} placeholder="Enter JO Status Remarks..." className="w-full border rounded px-3 py-2 text-sm mb-3 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500" />
        <label className="block text-sm font-medium mb-1">Cancellation Reasons <span className="text-red-500">*</span></label>
        <div className="space-y-1.5 mb-1">
          {CANCELLATION_REASON_OPTIONS.map((r) => (
            <label key={r} className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
              <input type="checkbox" checked={reasons.includes(r)} onChange={() => toggleReason(r)} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
              {r}
            </label>
          ))}
        </div>
        {reasons.length > 0 && remark.trim() && (
          <p className="text-xs text-gray-500 mt-2">Will save as: <span className="font-medium">{buildFinalRemark()}</span></p>
        )}
        {!canConfirm && (
          <p className="text-xs text-red-500 mt-2">
            {reasons.length === 0 && !remark.trim() ? "Please select at least one cancellation reason and enter a remark." : reasons.length === 0 ? "Please select at least one cancellation reason." : "Please enter a JO Status Remark."}
          </p>
        )}
        <div className="flex justify-end gap-2 mt-4">
          <button type="button" onClick={onCancel} className="px-4 py-2 text-sm border rounded hover:bg-gray-100">Cancel</button>
          <button type="button" disabled={!canConfirm} onClick={handleConfirmCancel} className="px-4 py-2 text-sm text-white rounded disabled:opacity-50 bg-red-600 hover:bg-red-700">Confirm</button>
        </div>
      </div>
      <ConflictDialog />
    </div>
  );
}

/* ───────────── Main Modal ───────────── */

export default function MigrationDetailModal({ recordId, onClose, onRefresh, onNapLocate = null }) {
  const { hasPerm, user } = useAuth();
  const canEdit = hasPerm("migration.edit");
  const canComplete = hasPerm("migration.complete");
  const canReschedule = hasPerm("migration.reschedule");
  const canCancel = hasPerm("migration.cancel");
  const canReopen = hasPerm("migration.reopen");
  const canReturnToCalling = hasPerm("migration.return_to_calling");
  const canPrioritize = hasPerm("migration.prioritize");

  // Field-level permissions (require both migration.edit AND specific field perm)
  const canEditDispatchDate = canEdit && hasPerm("mig.field.edit.date_visited");
  const canEditTeamNumber = canEdit && hasPerm("mig.field.edit.team_number");
  const canEditPersonnel = canEdit && hasPerm("mig.field.edit.personnel");
  const canEditDecomm = canEdit && hasPerm("mig.field.edit.decomm");
  const canEditRemarks = canEdit && hasPerm("mig.field.edit.remarks");
  const canEditCallStatus = canEdit && hasPerm("mig.field.edit.call_status");
  const canEditCallRemarks = canEdit && hasPerm("mig.field.edit.call_remarks");
  const canEditNapId = canEdit && hasPerm("mig.field.edit.nap_id");
  const canEditSubscriberInfo = canEdit && hasPerm("mig.field.edit.subscriber_info");

  const [record, setRecord] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState(null);
  const { check: checkComp, ConflictDialog } = useCompositionGuard();
  const [activeTab, setActiveTab] = useState("details");
  const [editingSubscriber, setEditingSubscriber] = useState(false);
  const [activePopup, setActivePopup] = useState(null); // null | "complete" | "reschedule" | "cancel"
  const [confirmReopen, setConfirmReopen] = useState(false);
  const [reopenCallRemarks, setReopenCallRemarks] = useState("");
  const [confirmReturnToCalling, setConfirmReturnToCalling] = useState(false);
  const [callCancelReasons, setCallCancelReasons] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [expandedPhoto, setExpandedPhoto] = useState(null); // blob URL of the completion photo

  const closePhoto = () => {
    setExpandedPhoto((url) => { if (url) URL.revokeObjectURL(url); return null; });
  };

  const openNapPhoto = async () => {
    try {
      const res = await api.get(`/migration/${recordId}/nap-photo`, { responseType: "blob" });
      setExpandedPhoto(URL.createObjectURL(res.data));
    } catch {
      setError("Completion photo is not available.");
    }
  };

  const parseJoStatus = (raw) => {
    if (!raw) return { status: "", extra: "" };
    const s = raw.trim();
    const patterns = [
      { match: /^completed/i, value: "Completed" },
      { match: /^cancell?ed/i, value: "Cancelled" },
      { match: /^rescheduled/i, value: "Rescheduled" },
      { match: /^pending/i, value: "Pending" },

    ];
    for (const p of patterns) {
      const m = s.match(p.match);
      if (m) {
        const rest = s.slice(m[0].length).replace(/^[\s\-–—:]+/, "").trim();
        return { status: p.value, extra: rest };
      }
    }
    return { status: "", extra: s };
  };

  const buildForm = (data) => {
    const { status, extra } = parseJoStatus(data.jo_status);
    let remarks = data.remarks || "";
    if (extra) remarks = remarks ? `${extra}\n${remarks}` : extra;
    return {
      team_number: data.team_number || "",
      personnel: data.personnel || "",
      personnel_1_id: data.personnel_1_id || null,
      personnel_2_id: data.personnel_2_id || null,
      jo_status: status,
      remarks,
      decomm: data.decomm || "N",
      modem_serial_number: data.modem_serial_number || "",
      iptv_serial_number: data.iptv_serial_number || "",
      nap_code: data.nap_code || "",
      nap_id: data.nap_id || "",
      nap_port: data.nap_port || "",
      vacant_ports: data.vacant_ports ?? "",
      call_status: data.call_status || "",
      call_remarks: data.call_remarks || "",
      call_status_other_text: data.call_status_other_text || "",
      dispatch_date: toInputDate(data.dispatch_date),
      oldacctno: data.oldacctno || "",
      name: data.name || "",
      playtype: data.playtype || "",
      bundletype: data.bundletype || "",
      municipalityname: data.municipalityname || "",
      brgy: data.brgy || "",
      sdu_mdu: data.sdu_mdu || "",
      oldnode: data.oldnode || "",
      address: data.address || "",
      village: data.village || "",
      contact_info: data.contact_info || "",
    };
  };

  useEffect(() => {
    let cancelled = false;
    api.get(`/migration/${recordId}`).then(({ data }) => {
      if (!cancelled) {
        setRecord(data); setForm(buildForm(data)); setLoading(false);
        // Match team_number to team master
        if (data.team_number) {
          api.get("/team-masters/active", { params: { company_id: user?.company_id } })
            .then(({ data: teams }) => {
              const match = teams.find((t) => t.team_number === data.team_number);
              if (match) setSelectedTeamId(match.id);
            }).catch(() => {});
        }
      }
    }).catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [recordId, user?.company_id]);

  const set = (field) => (val) => setForm((f) => ({ ...f, [field]: val }));
  const rv = (field) => record?.[field] || null;

  const handleSave = async () => {
    const ok = await checkComp({ teamNumber: form.team_number, assignmentDate: form.dispatch_date, personnel1Id: form.personnel_1_id, personnel2Id: form.personnel_2_id });
    if (!ok) return;
    setSaving(true); setError(""); setMessage(null);
    if (record?.workflow_stage === "for_calling_scheduling" && form.call_status) {
      if (form.call_status === "Others" && !form.call_status_other_text?.trim()) {
        setError("Please specify the call status details for 'Others'."); setSaving(false); return;
      }
      if (DISPATCH_REQUIRED_STATUSES.has(form.call_status) && !form.dispatch_date) {
        setError("Dispatch date is required for Confirmed."); setSaving(false); return;
      }
      if (form.call_status === "Cancelled" && callCancelReasons.length === 0) {
        setError("Please select at least one cancellation reason."); setSaving(false); return;
      }
    }
    try {
      const SUBSCRIBER_FIELDS = new Set(["oldacctno", "name", "playtype", "bundletype", "municipalityname", "brgy", "sdu_mdu", "oldnode", "address", "village", "contact_info"]);
      const payload = {};
      Object.entries(form).forEach(([k, v]) => {
        if (k === "jo_status") return;
        if (SUBSCRIBER_FIELDS.has(k) && !editingSubscriber) return;
        payload[k] = v === "" ? null : v;
      });
      // Build call_remarks for Cancelled: "reason1; reason2 - remarks" (matching shortcut flow)
      if (record?.workflow_stage === "for_calling_scheduling" && form.call_status === "Cancelled") {
        const remarksText = (form.call_remarks || "").trim();
        payload.call_remarks = remarksText
          ? `${callCancelReasons.join("; ")} - ${remarksText}`
          : callCancelReasons.join("; ");
      }
      const { data } = await api.put(`/migration/${recordId}`, payload);
      setRecord(data); setForm(buildForm(data));
      setEditingSubscriber(false);
      setMessage("Migration record updated successfully.");
      onRefresh();
    } catch (err) {
      setError(extractErrorMessage(err, "Save failed."));
    } finally { setSaving(false); }
  };

  /* ── NAP / Completion Details card save ── */

  const handleNapCardSave = async ({ napCode, napId, napPort, vacantPorts, modemSerial, iptvSerial, reason, napPhotoFile }) => {
    if (napPhotoFile) {
      const fd = new FormData();
      fd.append("file", napPhotoFile);
      await api.post(`/migration/${recordId}/nap-photo`, fd);
    }
    const { data } = await api.put(`/migration/${recordId}/nap-details`, {
      nap_code: napCode,
      nap_id: napId,
      nap_port: napPort,
      vacant_ports: vacantPortsPayload(vacantPorts),
      modem_serial_number: modemSerial,
      iptv_serial_number: iptvSerial || null,
      reason,
    });
    return data;
  };

  const handleNapCardSaved = (data) => {
    setRecord(data);
    setForm(buildForm(data));
    setMessage("NAP / Completion details updated successfully.");
    onRefresh();
  };

  /* ── Action handlers ── */

  const handleCompleteConfirm = async (body, napPhoto) => {
    // Keep the Complete dialog open (spinner showing) while the request — which
    // re-fetches NAP details from the external API — runs.
    setSaving(true); setError(""); setMessage(null);
    try {
      if (napPhoto) {
        const photoForm = new FormData();
        photoForm.append("file", napPhoto);
        await api.post(`/migration/${recordId}/nap-photo`, photoForm);
      }
      const { data } = await api.post(`/migration/${recordId}/complete`, body);
      setRecord(data); setForm(buildForm(data));
      setActivePopup(null);
      setMessage(
        data?.nap_details_updated
          ? "Migration record completed successfully and NAP details updated."
          : "Migration record completed successfully."
      );
      onRefresh();
    } catch (err) { setError(extractErrorMessage(err, "Failed to complete.")); }
    finally { setSaving(false); }
  };

  const handleRescheduleConfirm = async (body) => {
    setSaving(true); setActivePopup(null); setError(""); setMessage(null);
    try {
      const { data } = await api.post(`/migration/${recordId}/reschedule`, body);
      setRecord(data); setForm(buildForm(data));
      setMessage("Migration record rescheduled successfully.");
      onRefresh();
    } catch (err) { setError(extractErrorMessage(err, "Failed to reschedule.")); }
    finally { setSaving(false); }
  };

  const handleCancelConfirm = async (body) => {
    setSaving(true); setActivePopup(null); setError(""); setMessage(null);
    try {
      const { data } = await api.post(`/migration/${recordId}/cancel`, body);
      setRecord(data); setForm(buildForm(data));
      setMessage("Migration record cancelled.");
      onRefresh();
    } catch (err) { setError(extractErrorMessage(err, "Failed to cancel.")); }
    finally { setSaving(false); }
  };

  const handleReopen = async () => {
    setSaving(true);
    setConfirmReopen(false);
    setError("");
    setMessage(null);
    try {
      const { data } = await api.post(`/migration/${recordId}/reopen`, {
        call_remarks: reopenCallRemarks.trim(),
      });
      setRecord(data); setForm(buildForm(data));
      setReopenCallRemarks("");
      setMessage("Migration record reopened successfully.");
      onRefresh();
    } catch (err) { setError(extractErrorMessage(err, "Failed to reopen.")); }
    finally { setSaving(false); }
  };

  const handleReturnToCalling = async () => {
    setSaving(true);
    setConfirmReturnToCalling(false);
    setError("");
    setMessage(null);
    try {
      const { data } = await api.post(`/migration/${recordId}/return-to-calling`);
      setRecord(data); setForm(buildForm(data));
      setMessage("Migration record returned to Calling/Scheduling successfully.");
      onRefresh();
    } catch (err) { setError(extractErrorMessage(err, "Failed to return to Calling/Scheduling.")); }
    finally { setSaving(false); }
  };

  const handleTogglePriority = async () => {
    setSaving(true);
    setError("");
    setMessage(null);
    try {
      const { data } = await api.post(`/migration/${recordId}/prioritize`);
      setRecord(data); setForm(buildForm(data));
      setMessage(data.is_priority ? "Record marked as Priority." : "Priority flag removed.");
      onRefresh();
    } catch (err) { setError(extractErrorMessage(err, "Failed to update priority.")); }
    finally { setSaving(false); }
  };

  const isForDispatch = record?.workflow_stage === "for_dispatch";
  const hasTerminalAction = TERMINAL_ACTIONS.has(record?.action_result);
  const canPerformActions = isForDispatch && !hasTerminalAction;
  const isCancelled = record?.action_result === "Cancelled";
  const canShowReturnToCalling = canReturnToCalling && (record?.workflow_stage === "for_dispatch" || record?.workflow_stage === "visited_called");
  const historyCount = record?.reschedule_history?.length || 0;

  if (!record && !loading) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 pt-6 sm:pt-10 overflow-y-auto">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl mx-2 sm:mx-4 my-4 sm:my-8 flex flex-col max-h-[90vh]">

          {/* ── Header ── */}
          <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b shrink-0">
            <div className="flex items-center gap-3 min-w-0">
              <h2 className="text-lg font-semibold truncate">
                Migration Record Details{record?.name ? ` — ${record.name}` : ""}
              </h2>
              {record?.is_priority && (
                <span
                  title={record?.source_type === "PUBLIC_MIGRATION_REQUEST" ? "Submitted by subscriber via public migration form" : "Manually flagged as priority"}
                  className="flex-shrink-0 inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wide bg-amber-100 text-amber-800 border border-amber-300"
                >
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.955a1 1 0 00.95.69h4.16c.969 0 1.371 1.24.588 1.81l-3.365 2.445a1 1 0 00-.364 1.118l1.286 3.955c.3.921-.755 1.688-1.54 1.118L10 14.347l-3.365 2.446c-.784.57-1.838-.197-1.539-1.118l1.286-3.955a1 1 0 00-.364-1.118L2.654 8.157c-.783-.57-.38-1.81.588-1.81h4.16a1 1 0 00.95-.69l1.286-3.955z"/></svg>
                  Prio
                </span>
              )}
              {canPrioritize && (
                <button
                  onClick={handleTogglePriority}
                  disabled={saving}
                  title={record?.is_priority ? "Remove priority flag" : "Mark this record as priority"}
                  className={`px-3 py-1 text-sm rounded shrink-0 disabled:opacity-50 ${record?.is_priority ? "bg-gray-200 text-gray-700 hover:bg-gray-300" : "bg-amber-500 text-white hover:bg-amber-600"}`}
                >
                  {record?.is_priority ? "Unprioritize" : "Prioritize"}
                </button>
              )}
              {isCancelled && canReopen && (
                <button
                  onClick={() => setConfirmReopen(true)}
                  disabled={saving}
                  className="px-3 py-1 text-sm bg-amber-500 text-white rounded hover:bg-amber-600 disabled:opacity-50 shrink-0"
                >
                  Reopen
                </button>
              )}
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl leading-none ml-2">&times;</button>
          </div>

          {/* ── Mobile tab bar ── */}
          <div className="sm:hidden flex border-b shrink-0">
            <button onClick={() => setActiveTab("details")} className={`flex-1 py-2 text-sm font-medium text-center ${activeTab === "details" ? "border-b-2 border-blue-600 text-blue-600" : "text-gray-500"}`}>
              Details
            </button>
            <button onClick={() => setActiveTab("history")} className={`flex-1 py-2 text-sm font-medium text-center ${activeTab === "history" ? "border-b-2 border-blue-600 text-blue-600" : "text-gray-500"}`}>
              History {historyCount > 0 && <span className="ml-1 text-xs bg-gray-200 rounded-full px-1.5 py-0.5">{historyCount}</span>}
            </button>
          </div>

          {/* ── Body: sidebar + content ── */}
          <div className="flex flex-1 overflow-hidden min-h-0">

            {/* Sidebar — hidden on mobile */}
            <div className="hidden sm:flex w-48 shrink-0 border-r bg-gray-50 flex-col py-4 gap-1 px-2">
              <button
                onClick={() => setActiveTab("details")}
                className={`text-left px-3 py-2 rounded text-sm font-medium ${activeTab === "details" ? "bg-blue-600 text-white" : "text-gray-700 hover:bg-gray-200"}`}
              >
                Migration Record Detail
              </button>
              <button
                onClick={() => setActiveTab("history")}
                className={`text-left px-3 py-2 rounded text-sm font-medium flex items-center justify-between ${activeTab === "history" ? "bg-blue-600 text-white" : "text-gray-700 hover:bg-gray-200"}`}
              >
                <span>Movement History</span>
                {historyCount > 0 && (
                  <span className={`text-xs rounded-full px-1.5 py-0.5 ${activeTab === "history" ? "bg-white text-blue-700" : "bg-gray-300 text-gray-700"}`}>
                    {historyCount}
                  </span>
                )}
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4">
              {loading ? (
                <div className="text-center py-10 text-gray-400">Loading...</div>
              ) : activeTab === "details" ? (
                <>
                  {error && <div className="bg-red-50 border border-red-300 text-red-700 text-sm rounded px-3 py-2 mb-3">{error}</div>}

                  {/* ── Basic Record Info ── */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {record?.workflow_stage === "for_calling_scheduling" ? (
                      <Field label="Date Endorsed">
                        <ReadValue value={rv("endorsed_date")} />
                      </Field>
                    ) : (
                      <Field label="Dispatch Date">
                        <Input type="date" value={form.dispatch_date} onChange={set("dispatch_date")} disabled={!canEditDispatchDate} />
                      </Field>
                    )}
                    <Field label="JO Status">
                      <p className="text-sm text-gray-900 bg-gray-50 rounded px-3 py-2 border min-h-[36px]">
                        {form.jo_status || "Pending"}
                      </p>
                    </Field>
                    {record?.workflow_stage === "for_calling_scheduling" && (
                      <Field label="Dispatch Date">
                        {canEditCallStatus && DISPATCH_REQUIRED_STATUSES.has(form.call_status) ? (
                          <Input type="date" value={form.dispatch_date} onChange={set("dispatch_date")} disabled={!canEditCallStatus} />
                        ) : form.call_status === "Cancelled" && canEditCallStatus ? (
                          <Input type="date" value={getLocalToday()} disabled />
                        ) : (
                          <ReadValue value={rv("dispatch_date")} />
                        )}
                      </Field>
                    )}
                    <Field label="Decomm (Y/N)">
                      <Select value={form.decomm} onChange={set("decomm")} options={DECOMM_OPTIONS} disabled={!canEditDecomm} />
                    </Field>
                  </div>

                  {/* ── Team Assignment ── */}
                  <div className="mt-3">
                    <Field label="Team Number & Personnel">
                      {canEditTeamNumber ? (
                        <TeamPersonnelGroup
                          teamMasterId={selectedTeamId}
                          personnel1Id={form.personnel_1_id}
                          personnel2Id={form.personnel_2_id}
                          onTeamChange={(teamId, teamData) => {
                            setSelectedTeamId(teamId);
                            if (teamData) {
                              set("team_number")(teamData.team_number);
                            } else {
                              set("team_number")("");
                            }
                          }}
                          onPersonnel1Change={(id) => set("personnel_1_id")(id)}
                          onPersonnel2Change={(id) => set("personnel_2_id")(id)}
                          companyId={user?.company_id}
                          assignmentDate={form.dispatch_date || undefined}
                          disabledTeam={!canEditTeamNumber}
                          disabledPersonnel={!canEditPersonnel}
                        />
                      ) : (
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <span className="text-xs text-gray-500">Team Number</span>
                            <ReadValue value={form.team_number} />
                          </div>
                          <div>
                            <span className="text-xs text-gray-500">Personnel</span>
                            <ReadValue value={form.personnel} />
                          </div>
                        </div>
                      )}
                    </Field>
                  </div>

                  {/* ── JO / Account identifiers ── */}
                  {(record?.jo_number || record?.new_account_number) && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                      {record?.jo_number && (
                        <Field label="JO Number"><ReadValue value={rv("jo_number")} /></Field>
                      )}
                      {record?.new_account_number && (
                        <Field label="New Account Number"><ReadValue value={rv("new_account_number")} /></Field>
                      )}
                    </div>
                  )}

                  {/* ── NAP / Completion Details — grouped, permission-gated inline edit ── */}
                  {(() => {
                    const isTerminalMig =
                      ["Completed", "Cancelled", "Rescheduled"].includes(record?.jo_status) ||
                      ["Completed", "Cancelled", "Rescheduled"].includes(record?.action_result);
                    const isCompletedMig = record?.jo_status === "Completed" || record?.action_result === "Completed";
                    return (
                      <NapCompletionCard
                        napCode={form.nap_code}
                        napId={form.nap_id}
                        napPort={form.nap_port}
                        vacantPorts={form.vacant_ports}
                        modemSerial={form.modem_serial_number}
                        iptvSerial={form.iptv_serial_number}
                        hasPhoto={!!record?.has_nap_photo}
                        isTerminal={isTerminalMig}
                        completed={isCompletedMig}
                        canEdit={isTerminalMig ? hasPerm("migration.edit_nap_terminal") : canEditNapId}
                        onViewPhoto={openNapPhoto}
                        renderNapCode={
                          isCompletedMig && form.nap_code && onNapLocate
                            ? () => (
                                <button
                                  type="button"
                                  onClick={() => onNapLocate(form.nap_code)}
                                  title="View this NAP on the map"
                                  className="w-full text-left text-sm font-medium text-blue-700 bg-gray-50 rounded px-3 py-2 border hover:bg-blue-50 hover:border-blue-300 inline-flex items-center justify-between gap-2"
                                >
                                  <span className="underline decoration-dotted">{form.nap_code}</span>
                                  <svg className="w-4 h-4 flex-shrink-0 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a2 2 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                  </svg>
                                </button>
                              )
                            : undefined
                        }
                        onSave={handleNapCardSave}
                        onSaved={handleNapCardSaved}
                      />
                    );
                  })()}

                  {/* ── JO Remarks (full width) ── */}
                  <div className="mt-3">
                    <Field label="JO Remarks">
                      <textarea value={form.remarks ?? ""} onChange={(e) => set("remarks")(e.target.value)} disabled={!canEditRemarks} rows={2} className="w-full border rounded px-3 py-2 text-sm resize-none disabled:bg-gray-50 disabled:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Job order status remarks" />
                    </Field>
                  </div>

                  {/* ── Call Status Information ── */}
                  {(() => {
                    const isCalling = record?.workflow_stage === "for_calling_scheduling";
                    const showCallSection = isCalling || record?.call_status;
                    if (!showCallSection) return null;
                    return (
                      <div className="border rounded-lg mt-3 px-4 py-3">
                        <h4 className="text-sm font-medium text-gray-700 mb-3">Call Status Information</h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <Field label="Call Status">
                            {isCalling && canEditCallStatus ? (
                              <Select
                                value={form.call_status}
                                onChange={(v) => { set("call_status")(v); if (v !== "Others") set("call_status_other_text")(""); if (DISPATCH_REQUIRED_STATUSES.has(v)) set("dispatch_date")(getLocalTomorrow()); else set("dispatch_date")(""); if (v !== "Cancelled") setCallCancelReasons([]); }}
                                options={CALL_STATUS_OPTIONS}
                                disabled={!canEditCallStatus}
                              />
                            ) : (
                              <ReadValue value={fmtCallStatus(record)} />
                            )}
                          </Field>
                          {form.call_status === "Others" && isCalling && canEditCallStatus && (
                            <Field label="Others (specify)">
                              <Input value={form.call_status_other_text} onChange={set("call_status_other_text")} disabled={!canEditCallStatus} placeholder="Specify call status" />
                            </Field>
                          )}
                          {record?.call_attempt_count > 0 && (
                            <Field label="Call Attempts"><ReadValue value={`${record.call_attempt_count} attempt(s)`} /></Field>
                          )}
                        </div>
                        {form.call_status === "Cancelled" && isCalling && canEditCallStatus && (
                          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                            <Field label={<>Cancellation Reasons <span className="text-red-500">*</span></>}>
                              <div className="space-y-1.5">
                                {CALL_CANCEL_REASON_OPTIONS.map((reason) => (
                                  <label key={reason} className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
                                    <input type="checkbox" checked={callCancelReasons.includes(reason)} onChange={() => setCallCancelReasons((prev) => prev.includes(reason) ? prev.filter((r) => r !== reason) : [...prev, reason])} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                                    {reason}
                                  </label>
                                ))}
                              </div>
                              {callCancelReasons.length === 0 && (
                                <p className="text-xs text-red-500 mt-1">Please select at least one cancellation reason.</p>
                              )}
                              <p className="text-xs text-amber-600 mt-2">Setting call status to Cancelled will also cancel this migration record.</p>
                            </Field>
                          </div>
                        )}
                        <div className="mt-3">
                          <Field label="Call Remarks">
                            {isCalling && canEditCallRemarks ? (
                              <textarea value={form.call_remarks ?? ""} onChange={(e) => set("call_remarks")(e.target.value)} disabled={!canEditCallRemarks} rows={2} className="w-full border rounded px-3 py-2 text-sm resize-none disabled:bg-gray-50 disabled:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Call remarks" />
                            ) : (
                              <ReadValue value={rv("call_remarks")} />
                            )}
                          </Field>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Subscriber Information */}
                  <CollapsibleSection
                    title={
                      <span className="flex items-center gap-2">
                        Subscriber Information
                        {canEditSubscriberInfo && !editingSubscriber && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setEditingSubscriber(true); }}
                            className="px-2 py-0.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                          >
                            Edit
                          </button>
                        )}
                        {canEditSubscriberInfo && editingSubscriber && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingSubscriber(false);
                              setForm((f) => ({
                                ...f,
                                oldacctno: record?.oldacctno || "",
                                name: record?.name || "",
                                playtype: record?.playtype || "",
                                bundletype: record?.bundletype || "",
                                municipalityname: record?.municipalityname || "",
                                brgy: record?.brgy || "",
                                sdu_mdu: record?.sdu_mdu || "",
                                oldnode: record?.oldnode || "",
                                address: record?.address || "",
                                village: record?.village || "",
                                contact_info: record?.contact_info || "",
                              }));
                            }}
                            className="px-2 py-0.5 text-xs border border-gray-300 text-gray-600 rounded hover:bg-gray-100"
                          >
                            Cancel Edit
                          </button>
                        )}
                      </span>
                    }
                    defaultOpen
                  >
                    {editingSubscriber ? (
                      <>
                        <Field label="Old Account No."><Input value={form.oldacctno} onChange={set("oldacctno")} /></Field>
                        <Field label="Name"><Input value={form.name} onChange={set("name")} /></Field>
                        <Field label="Play Type"><Input value={form.playtype} onChange={set("playtype")} /></Field>
                        <Field label="Bundle Type"><Input value={form.bundletype} onChange={set("bundletype")} /></Field>
                        <Field label="Municipality"><Input value={form.municipalityname} onChange={set("municipalityname")} /></Field>
                        <Field label="Barangay"><Input value={form.brgy} onChange={set("brgy")} /></Field>
                        <Field label="SDU/MDU"><Input value={form.sdu_mdu} onChange={set("sdu_mdu")} /></Field>
                        <Field label="Old Node"><Input value={form.oldnode} onChange={set("oldnode")} /></Field>
                        <div className="md:col-span-2">
                          <Field label="Address"><Input value={form.address} onChange={set("address")} /></Field>
                        </div>
                        <Field label="Village"><Input value={form.village} onChange={set("village")} /></Field>
                        <Field label="Contact Info"><Input value={form.contact_info} onChange={set("contact_info")} /></Field>
                      </>
                    ) : (
                      <>
                        <MetaField label="Old Account No." value={rv("oldacctno")} />
                        <MetaField label="Name" value={rv("name")} />
                        <MetaField label="Play Type" value={rv("playtype")} />
                        <MetaField label="Bundle Type" value={rv("bundletype")} />
                        <MetaField label="Municipality" value={rv("municipalityname")} />
                        <MetaField label="Barangay" value={rv("brgy")} />
                        <MetaField label="SDU/MDU" value={rv("sdu_mdu")} />
                        <MetaField label="Old Node" value={rv("oldnode")} />
                        <div className="md:col-span-2">
                          <MetaField label="Address" value={rv("address")} />
                        </div>
                        <MetaField label="Village" value={rv("village")} />
                        <MetaField label="Contact Info" value={rv("contact_info")} />
                      </>
                    )}
                  </CollapsibleSection>

                  <CollapsibleSection title="Network / Service Information">
                    <MetaField label="Type of Customer" value={rv("typeofcustomer")} />
                    <MetaField label="Subscriber Status" value={rv("subscriberstatusname")} />
                    <MetaField label="Network Type" value={rv("networktype")} />
                    <MetaField label="Service Type (Tech)" value={rv("servicetype_based_on_tech")} />
                    <MetaField label="Deployment Type" value={rv("deployment_type")} />
                    <MetaField label="Node No (SKY)" value={rv("nodeno_sky")} />
                    <MetaField label="Quadrant" value={rv("quadrant")} />
                  </CollapsibleSection>

                  <CollapsibleSection title="Migration / Completion">
                    <MetaField label="Migration" value={rv("migration")} />
                    <MetaField label="Migration2" value={rv("migration2")} />
                    <MetaField label="Date Completed" value={rv("date_completed")} />
                    <MetaField label="Month Completed" value={rv("month_completed")} />
                    <MetaField label="Week Completed" value={rv("week_completed")} />
                    <MetaField label="Year Completed" value={rv("year_completed")} />
                    <MetaField label="MSP" value={rv("msp")} />
                    <MetaField label="MSP Cluster" value={rv("msp_cluster")} />
                  </CollapsibleSection>

                  <CollapsibleSection title="Decommission">
                    <MetaField label="Decom Date" value={rv("decom_date")} />
                    <MetaField label="Year Decom" value={rv("year_decom")} />
                    <MetaField label="Decom Sched" value={rv("decom_sched")} />
                    <MetaField label="Decom Week" value={rv("decom_week")} />
                  </CollapsibleSection>

                  <CollapsibleSection title="Location / Area">
                    <MetaField label="Old Hub" value={rv("oldhub")} />
                    <MetaField label="New Quad" value={rv("newquad")} />
                    <MetaField label="Sector" value={rv("sector")} />
                    <MetaField label="Area" value={rv("area")} />
                    <MetaField label="FMA" value={rv("fma")} />
                    <MetaField label="Date2" value={rv("date2")} />
                    <MetaField label="Year2" value={rv("year2")} />
                  </CollapsibleSection>
                </>
              ) : (
                /* ── Reschedule History Tab ── */
                <div>
                  {historyCount === 0 ? (
                    <p className="text-sm text-gray-500 py-6 text-center">No movement history.</p>
                  ) : (
                    <div className="space-y-4">
                      {record.reschedule_history
                        .slice()
                        .sort((a, b) => new Date(b.rescheduled_at) - new Date(a.rescheduled_at))
                        .map((entry, idx) => {
                          let parsedReasons = [];
                          try { parsedReasons = JSON.parse(entry.reschedule_reasons || "[]"); } catch { /* ignore */ }
                          return (
                            <div key={entry.id} className="border rounded-lg p-4 bg-gray-50">
                              <p className="text-xs font-semibold text-gray-500 mb-3">
                                Entry {historyCount - idx} &mdash; {new Date(entry.rescheduled_at).toLocaleString()}
                              </p>
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                {entry.date_visited && <MetaField label="Dispatch Date" value={entry.date_visited} />}
                                <MetaField label="Dispatch Date" value={entry.dispatch_date} />
                                {entry.endorsed_date && <MetaField label="Date Endorsed" value={entry.endorsed_date} />}
                                <MetaField label="Team Number" value={entry.team_number} />
                                <MetaField label="Personnel" value={entry.personnel} />
                                <MetaField label="JO Status" value={entry.jo_status} />
                                <div className="md:col-span-2"><MetaField label="Remarks" value={entry.remarks} /></div>
                                {parsedReasons.length > 0 && (
                                  <div className="md:col-span-3"><MetaField label="Reschedule Reasons" value={parsedReasons.join("; ")} /></div>
                                )}
                                {entry.call_status && <MetaField label="Call Status" value={entry.call_status} />}
                                {entry.call_remarks && <div className="md:col-span-2"><MetaField label="Call Remarks" value={entry.call_remarks} /></div>}
                                {entry.call_status_other_text && <MetaField label="Call Status (Other)" value={entry.call_status_other_text} />}
                                {entry.action_result && <MetaField label="Action Result" value={entry.action_result} />}
                                {entry.action_at && <MetaField label="Action At" value={new Date(entry.action_at).toLocaleString()} />}
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* ── Message bar ── */}
          {message && (
            <div className="px-4 sm:px-6 py-2 shrink-0">
              <p className="text-sm text-green-700 bg-green-50 rounded px-3 py-2">{message}</p>
            </div>
          )}

          {/* ── Footer action buttons ── */}
          <div className="sticky bottom-0 flex items-center justify-end gap-2 px-4 sm:px-6 py-3 sm:py-4 border-t bg-white shrink-0 z-10">
            {canShowReturnToCalling && (
              <button onClick={() => setConfirmReturnToCalling(true)} disabled={saving} className="px-4 py-2 text-sm border border-purple-400 text-purple-700 rounded hover:bg-purple-50 disabled:opacity-50">
                Return to Calling/Scheduling
              </button>
            )}
            {canPerformActions && canComplete && (
              <button onClick={() => setActivePopup("complete")} className="px-4 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-700">
                Complete Job Order
              </button>
            )}
            {canPerformActions && canReschedule && (
              <button onClick={() => setActivePopup("reschedule")} className="px-4 py-2 text-sm border border-yellow-400 text-yellow-700 rounded hover:bg-yellow-50">
                Reschedule Job Order
              </button>
            )}
            {canPerformActions && canCancel && (
              <button onClick={() => setActivePopup("cancel")} className="px-4 py-2 text-sm border border-red-300 text-red-600 rounded hover:bg-red-50">
                Cancel Job Order
              </button>
            )}
            {canEdit && activeTab === "details" && (
              <button onClick={handleSave} disabled={saving || loading} className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
                {saving ? "Saving…" : "Save Changes"}
              </button>
            )}
            <button onClick={onClose} className="px-4 py-2 text-sm border rounded hover:bg-gray-100">Close</button>
          </div>
        </div>
      </div>

      {/* ── Popup dialogs ── */}
      {activePopup === "complete" && <CompleteDialog record={record} companyId={user?.company_id} onConfirm={handleCompleteConfirm} onCancel={() => setActivePopup(null)} submitting={saving} />}
      {activePopup === "reschedule" && <RescheduleDialog record={record} companyId={user?.company_id} onConfirm={handleRescheduleConfirm} onCancel={() => setActivePopup(null)} />}
      {activePopup === "cancel" && <CancelDialog record={record} companyId={user?.company_id} onConfirm={handleCancelConfirm} onCancel={() => setActivePopup(null)} />}

      {confirmReopen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 p-6">
            <h3 className="text-lg font-semibold mb-2">Reopen Migration Record</h3>
            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to reopen this cancelled migration record? The status will be changed back to <span className="font-semibold text-blue-700">Pending</span> and the dispatch date will be set to today.
            </p>
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Call Status</label>
              <input type="text" value="Confirmed" disabled className="w-full border rounded px-3 py-2 text-sm bg-gray-100 text-gray-600" />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Call Remarks <span className="text-red-500">*</span></label>
              <textarea
                value={reopenCallRemarks}
                onChange={(e) => setReopenCallRemarks(e.target.value)}
                placeholder="Enter call remarks"
                rows={3}
                className="w-full border rounded px-3 py-2 text-sm focus:ring-1 focus:ring-amber-400 focus:border-amber-400"
              />
              {reopenCallRemarks.length > 0 && !reopenCallRemarks.trim() && (
                <p className="text-xs text-red-500 mt-1">Call remarks cannot be blank.</p>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => { setConfirmReopen(false); setReopenCallRemarks(""); }} className="px-4 py-2 text-sm border rounded hover:bg-gray-100">Cancel</button>
              <button type="button" onClick={handleReopen} disabled={!reopenCallRemarks.trim()} className="px-4 py-2 text-sm text-white rounded bg-amber-500 hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed">Confirm Reopen</button>
            </div>
          </div>
        </div>
      )}

      {confirmReturnToCalling && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 p-6">
            <h3 className="text-lg font-semibold mb-2">Return to Calling/Scheduling</h3>
            <p className="text-sm text-gray-600 mb-4">
              This will return the record to the <span className="font-semibold text-purple-700">For Calling/Scheduling</span> page. The current status and remarks will be saved to Movement History, and the record will be reset to a <span className="font-semibold text-blue-700">Pending</span> calling state.
            </p>
            <p className="text-xs text-gray-500 mb-4">
              Team Number and Personnel assignments will be retained. JO Status, JO Remarks, Call Status, and Call Remarks will be cleared.
            </p>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setConfirmReturnToCalling(false)} className="px-4 py-2 text-sm border rounded hover:bg-gray-100">Cancel</button>
              <button type="button" onClick={handleReturnToCalling} className="px-4 py-2 text-sm text-white rounded bg-purple-600 hover:bg-purple-700">Confirm Return</button>
            </div>
          </div>
        </div>
      )}

      {/* Completion photo lightbox */}
      {expandedPhoto && (
        <div className="fixed inset-0 z-[10002] flex items-center justify-center bg-black/80" onClick={closePhoto}>
          <img src={expandedPhoto} alt="Completion Photo" className="max-w-[90vw] max-h-[90vh] object-contain rounded shadow-2xl" />
          <button className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-white/20 text-white text-xl hover:bg-white/40" onClick={closePhoto}>×</button>
        </div>
      )}

      <ConflictDialog />
    </>
  );
}
